import * as React from 'react';
import { useTranslation } from 'react-i18next';
import chasLogo from '../../public/assets/images/chas-logo.svg';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Box, Button, Link, Typography } from '@material-ui/core';
import PhoneIcon from '@material-ui/icons/Phone';
import EmailIcon from '@material-ui/icons/Email';
import HelpCenterIcon from '@material-ui/icons/Help';
import { useAppStateContext } from '../contexts/AppState';
import { getCountrySpecificConfig } from '../helpers/helpInfo';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    helpPanelRoot: {
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      padding: 18,
      boxSizing: 'border-box'
    },
    infoWrapper: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      padding: 18,
      boxSizing: 'border-box'
    },
    title: {
      fontSize: 22,
      fontWeight: 600,
      lineHeight: '28px',
      letterSpacing: '0.016em',
      color: 'var(--list-secondary)',
      marginBottom: 11
    },
    text: {
      fontSize: 14,
      lineHeight: '20px',
      whiteSpace: 'pre-line',
      letterSpacing: '0.016em',
      color: 'var(--list-secondary)'
    },
    workingHours: {
      fontSize: 14,
      lineHeight: '20px',
      whiteSpace: 'pre-line',
      marginTop: 9
    },
    contacts: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      marginTop: 36
    },
    contactItem: {
      display: 'flex'
    },
    icon: {
      color: 'var(--primary)',
      marginRight: 10
    },
    link: {
      fontSize: 16,
      lineHeight: '24px',
      fontWeight: 600,
      letterSpacing: '0.009em',
      '&> a': {
        color: 'var(--primary)'
      }
    },
    buttons: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      marginTop: 48
    },
    button: {
      textTransform: 'none',
      borderRadius: '100px',
      color: 'var(--primary)',
      borderColor: 'var(--light-outline)',
      height: 38,
      fontWeight: 600,
      letterSpacing: '0.006em'
    },
    chasLogo: {
      width: 228,
      height: 99,
      marginTop: 28,
      marginBottom: 65,
      [theme.breakpoints.down('md')]: {
        display: 'none'
      }
    }
  })
);

export const HelpPanel: React.FC = () => {
  const { t } = useTranslation('signup');
  const classes = useStyles();
  const { formData } = useAppStateContext();
  const { phone, workingHours, email, faqUrl, loginUrl, returnUrl } = getCountrySpecificConfig(formData.packageType);

  return (
    <Box className={classes.helpPanelRoot}>
      <img className={classes.chasLogo} src={chasLogo} alt={t('helpPanel.chasLogoAlt', 'Veriforce CHAS')} />
      <Box className={classes.infoWrapper}>
        <Typography className={classes.title} variant="h5">
          {t('helpPanel.title', 'Need any help?')}
        </Typography>
        <Typography className={classes.text} variant="body2">
          {t('helpPanel.hint', 'Get in touch for more help from our\n award-winning team.')}
        </Typography>
        <Box className={classes.contacts}>
          <Box className={classes.contactItem}>
            <PhoneIcon className={classes.icon} />
            <Box>
              <Typography className={classes.link} variant="h6">
                <Link href={`tel:${phone}`}>{phone}</Link>
              </Typography>
              <Typography className={classes.workingHours} variant="body2">
                {t('helpPanel.workingHours', {
                  defaultValue: '{{workingHours}}',
                  workingHours
                })}
              </Typography>
            </Box>
          </Box>
          <Box className={classes.contactItem}>
            <EmailIcon className={classes.icon} />
            <Typography className={classes.link} variant="h6">
              <Link href={`mailto:${email}`}>{email}</Link>
            </Typography>
          </Box>
          <Box className={classes.contactItem}>
            <HelpCenterIcon className={classes.icon} />
            <Typography className={classes.link} variant="h6">
              <Link href={faqUrl} target="_blank">
                {t('helpPanel.faqs', 'FAQs')}
              </Link>
            </Typography>
          </Box>
        </Box>
        <Box className={classes.buttons}>
          <Button className={classes.button} variant="outlined" href={loginUrl}>
            {t('helpPanel.loginChas', 'Log in to Veriforce CHAS')}
          </Button>
          <Button className={classes.button} variant="outlined" href={returnUrl}>
            {t('helpPanel.returnChas', 'Return to CHAS website')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
